<template>
  <!-- Cover Image Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="true"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Cover Image</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="coverImageUrl" class="image-preview-container">
          <img :src="coverImageUrl" alt="Cover Preview" class="image-preview" />
          <button class="remove-image-button" @click="removeCoverImage">
            <v-icon>mdi-delete</v-icon>
            Remove Image
          </button>
        </div>
        <div v-else class="empty-image-message">
          No cover image selected. Please upload an image for your lesson plan.
        </div>
        <label class="upload-button">
          {{ coverImageUrl ? "Change Image" : "Upload Image" }}
          <input
            ref="fileInputRef"
            type="file"
            @change="handleCoverImageUpload"
            accept="image/*"
            style="display: none"
          />
        </label>
      </div>
    </template>
  </AccordionItem>

  <!-- Loading State -->
  <div v-if="isLoading" class="loading-container">
    <p>Loading categories...</p>
  </div>

  <!-- Empty State -->
  <div v-else-if="tagSections.length === 0" class="empty-state">
    <p>No categories available.</p>
  </div>

  <!-- Dynamic Tag Sections -->
  <AccordionItem
    v-else
    v-for="section in tagSections"
    :key="section.id"
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    @toggle="(isOpen) => handleAccordionToggle(isOpen, section.id)"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">{{ section.title }}</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <!-- Selected tags display -->
        <div class="tags-container" v-if="section.selectedItems.length > 0">
          <div
            v-for="(tag, index) in section.selectedItems"
            :key="index"
            class="tag"
          >
            <p>{{ getTagDisplayText(tag, section) }}</p>
            <button
              @click="removeTag(section, index)"
              class="delete-tag-button"
            >
              <v-icon>mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div v-else class="empty-tags-message">No tags selected</div>

        <div class="select-container">
          <MultipleSelect
            v-model="section.selectedValues"
            :placeholder="`Select ${section.title}...`"
            :options="section.formattedOptions"
            maxHeight="200px"
            variant="form"
            :teleport="true"
            :match-trigger-width="true"
            @update:modelValue="handleMultipleSelectionChange(section)"
          />
        </div>

        <div v-if="section.isLoadingOptions" class="options-loading-indicator">
          Loading options...
        </div>
      </div>
    </template>
  </AccordionItem>

  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Permalink</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div class="permalink-container">
          <BaseInput
            type="text"
            class="permalink-input"
            v-model="permalink"
            @input="updatePermalink"
            placeholder="Enter permalink"
          />
        </div>
      </div>
    </template>
  </AccordionItem>

  <!-- SCORM Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
    @toggle="(isOpen) => handleScormAccordionToggle(isOpen)"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">SCORM</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <!-- SCORM Method Toggle -->
        <div class="scorm-method-toggle">
          <button
            :class="[
              'method-button',
              { 'tab-active': scormMethod === 'select' },
            ]"
            @click="setScormMethod('select')"
          >
            Select Existing
          </button>
          <button
            :class="[
              'method-button',
              { 'tab-active': scormMethod === 'upload' },
            ]"
            @click="setScormMethod('upload')"
          >
            Upload New
          </button>
        </div>

        <!-- Selected/Uploaded SCORM display -->
        <div
          v-if="selectedScorm || uploadedScorm"
          class="selected-scorm-container"
        >
          <div class="selected-scorm-item">
            <div class="scorm-info">
              <h4>
                {{
                  (selectedScorm && selectedScorm.title) ||
                  (uploadedScorm && uploadedScorm.name)
                }}
              </h4>
              <p v-if="scormMethod === 'upload'" class="scorm-type">
                Uploaded File
              </p>
              <p v-else class="scorm-type">Existing Package</p>
            </div>
            <button
              @click="removeScorm"
              class="remove-scorm-button"
              title="Remove SCORM package"
            >
              <v-icon>mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div v-else class="empty-scorm-message">No SCORM package selected</div>

        <!-- Select Method Content -->
        <div v-if="scormMethod === 'select'" class="select-container">
          <SingleSelect
            v-model="selectedScormValue"
            placeholder="Select SCORM Package..."
            :options="scormOptions"
            variant="form"
            :teleport="true"
            :match-trigger-width="true"
            @update:modelValue="handleScormSelection"
          />
        </div>

        <!-- Upload Method Content -->
        <div v-if="scormMethod === 'upload'" class="upload-container">
          <button
            class="upload-scorm-button"
            @click="openUploadModal"
            :disabled="!!uploadedScorm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            {{ uploadedScorm ? "Change SCORM File" : "Upload SCORM File" }}
          </button>
        </div>

        <div v-if="isLoadingScorm" class="scorm-loading-indicator">
          Loading SCORM packages...
        </div>
      </div>
    </template>
  </AccordionItem>

  <!-- SCORM Upload Modal -->
  <ScormUploadModal
    :isOpen="showUploadModal"
    @close="closeUploadModal"
    @upload="handleScormUpload"
    ref="uploadModalRef"
  />
</template>

<script setup>
import BaseInput from "@/components/BaseInput.vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import MultipleSelect from "@/components/common/MultipleSelect.vue";
import categoryAdminService from "@/services/categoryAdminService";
import scormFilesService from "@/services/scormFilesService";
import { CATEGORY_TYPES } from "@/utils/constants";
import AccordionItem from "@/views/ChinaUs/AccordionItem.vue";
import ScormUploadModal from "@/components/modal/ScormUploadModal.vue";
import {
  defineEmits,
  defineExpose,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";

const emit = defineEmits([
  "cover-image-updated",
  "tag-sections-updated",
  "permalink-updated",
  "scorm-updated",
]);

const coverImageUrl = ref(null);
const fileInputRef = ref(null); // Reference to the file input element
const isLoading = ref(true);
const permalink = ref(""); // Add permalink reference
const loadedOptions = ref(new Set()); // Track which categories have loaded options
const standardsLoaded = ref(false); // Track if standards are loaded
const standardsLoading = ref(false); // Track if standards are currently loading

// SCORM related reactive data
const selectedScorm = ref(null);
const selectedScormValue = ref("");
const scormOptions = ref([]);
const isLoadingScorm = ref(false);
const scormLoaded = ref(false);

// SCORM upload related data
const scormMethod = ref("select"); // 'select' or 'upload'
const uploadedScorm = ref(null);
const showUploadModal = ref(false);
const uploadModalRef = ref(null);

// Standards data structure
const standardsSection = reactive({
  id: "standards", // Use a special ID for the standards section
  title: "Standards",
  options: [], // Will hold grouped standards
  formattedOptions: [], // Options formatted for BaseInput
  selectedItems: [], // Text representation of selected items
  selectedValues: [], // Values for v-model binding
  isLoadingOptions: false,
  groupedOptions: {}, // Will contain all standards grouped by category
});

const handleCoverImageUpload = (event) => {
  const file = event.target.files[0];
  if (file && file.type.startsWith("image/")) {
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image file size should be less than 5MB.");
      event.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      coverImageUrl.value = e.target.result;
      emit("cover-image-updated", coverImageUrl.value);
    };
    reader.readAsDataURL(file);
  } else if (file) {
    // Only show error if a file was actually selected
    // Handle invalid file type
    alert("Please select an image file.");
    // Reset file input
    event.target.value = null;
  }
};

// Add function to remove the cover image
const removeCoverImage = () => {
  coverImageUrl.value = null;
  emit("cover-image-updated", null);

  // Reset the file input element to ensure it can trigger onChange for the same file
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

// Define the tag sections with initial empty state
const tagSections = reactive([]);

// Fetch categories from API
const fetchCategories = async () => {
  try {
    isLoading.value = true;
    const response = await categoryAdminService.getCategoriesLesson();

    if (response && response.data.data && response.data.data.rows) {
      // Clear existing tag sections
      tagSections.splice(0, tagSections.length);

      // Create tag sections from categories
      for (const category of response.data.data.rows) {
        const isLessonPlanCategory =
          category.category_for?.some(
            (item) => item === CATEGORY_TYPES.ProfessionalDevelopment
          ) || false;

        if (!isLessonPlanCategory) continue;

        const section = {
          id: category.id,
          title: category.title,
          options: [],
          formattedOptions: [], // Options formatted for BaseInput
          selectedItems: [], // Text representation of selected items
          selectedValues: [], // Values for v-model binding
          isLoadingOptions: false,
        };

        tagSections.push(section);
      }
    }
  } catch (error) {
    console.error("Error fetching categories:", error);
  } finally {
    isLoading.value = false;
  }
};

// Format options for BaseInput component
const formatOptions = (options, isStandard = false) => {
  if (isStandard) {
    const formattedOptions = [];

    // Group headers as disabled options
    for (const [category, terms] of Object.entries(
      standardsSection.groupedOptions
    )) {
      // Add group header
      formattedOptions.push({
        text: `--- ${category} ---`,
        value: `group_${category}`,
        disabled: true,
      });

      // Add standard items
      for (const term of terms) {
        formattedOptions.push({
          text: term.title,
          value: JSON.stringify({
            id: term.id,
            title: term.title,
            standardCategory: term.standardCategory,
          }),
        });
      }
    }

    return formattedOptions;
  } else {
    // Regular options formatting
    return options.map((option) => ({
      text: option.title || option,
      value: JSON.stringify({
        id: option.id || option,
        title: option.title || option,
      }),
    }));
  }
};

// Get display text for a tag based on its value
const getTagDisplayText = (tagValue, section) => {
  try {
    const parsedValue = JSON.parse(tagValue);
    if (section.id === "standards" && parsedValue.standardCategory) {
      return `${parsedValue.standardCategory}: ${parsedValue.title}`;
    }
    return parsedValue.title;
  } catch (e) {
    // Fallback for any unparseable values
    return tagValue;
  }
};

// Fetch options for a specific category
const fetchCategoryOptions = async (categoryId) => {
  // Skip if options are already loaded or currently loading
  const sectionIndex = tagSections.findIndex(
    (section) => section.id === categoryId
  );
  if (
    sectionIndex === -1 ||
    loadedOptions.value.has(categoryId) ||
    tagSections[sectionIndex].isLoadingOptions
  ) {
    return;
  }

  try {
    // Set loading state for this specific category
    tagSections[sectionIndex].isLoadingOptions = true;

    const response = await categoryAdminService.getById(categoryId);

    if (response && response.data.data && response.data.data.terms) {
      // Update options with term objects containing id and title
      const filteredTerms = response.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      const terms = filteredTerms.map((term) => ({
        id: term.id,
        title: term.title,
      }));
      tagSections[sectionIndex].options = terms;

      // Format options for BaseInput
      tagSections[sectionIndex].formattedOptions = formatOptions(terms);

      // Mark this category as having loaded options
      loadedOptions.value.add(categoryId);
    }
  } catch (error) {
    console.error(`Error fetching options for category ${categoryId}:`, error);
  } finally {
    if (sectionIndex !== -1) {
      tagSections[sectionIndex].isLoadingOptions = false;
    }
  }
};

// Fetch standards categories
const fetchStandardsCategories = async () => {
  if (standardsLoaded.value || standardsLoading.value) {
    return;
  }

  try {
    standardsSection.isLoadingOptions = true;
    standardsLoading.value = true;

    const response = await categoryAdminService.getStandardCategories();
    console.log("Standards response:", response);

    if (
      response &&
      response.data &&
      response.data.data &&
      response.data.data.rows
    ) {
      const standardCategories = response.data.data.rows;

      // Clear existing grouped options
      standardsSection.groupedOptions = {};

      // Fetch options for each standard category
      for (const standardCategory of standardCategories) {
        await fetchStandardCategoryOptions(standardCategory);
      }

      // Format options for BaseInput
      standardsSection.formattedOptions = formatOptions([], true);

      // Mark standards as loaded
      standardsLoaded.value = true;
    }
  } catch (error) {
    console.error("Error fetching standard categories:", error);
  } finally {
    standardsSection.isLoadingOptions = false;
    standardsLoading.value = false;
  }
};

// Fetch options for a specific standard category
const fetchStandardCategoryOptions = async (standardCategory) => {
  try {
    const response = await categoryAdminService.getById(standardCategory.id);

    if (response && response.data.data && response.data.data.terms) {
      // Store terms for this standard category
      standardsSection.groupedOptions[standardCategory.title] =
        response.data.data.terms.map((term) => ({
          id: term.id,
          title: term.title,
          standardCategory: standardCategory.title,
        }));

      // Update formatted options for BaseInput
      standardsSection.formattedOptions = formatOptions([], true);
    }
  } catch (error) {
    console.error(
      `Error fetching options for standard category ${standardCategory.id}:`,
      error
    );
  }
};

// Handle accordion toggle to load options when opened
const handleAccordionToggle = (isOpen, categoryId) => {
  console.log("handleAccordionToggle", isOpen, categoryId);
  if (isOpen) {
    if (categoryId === "standards") {
      fetchStandardsCategories();
    } else if (!loadedOptions.value.has(categoryId)) {
      fetchCategoryOptions(categoryId);
    }
  }
};

// Fetch SCORM files from API
const fetchScormFiles = async () => {
  if (scormLoaded.value || isLoadingScorm.value) {
    return;
  }

  try {
    isLoadingScorm.value = true;
    const response = await scormFilesService.getAll();

    console.log("SCORM response:", response);

    if (response && response.data && Array.isArray(response.data)) {
      // Format SCORM files for BaseInput options
      scormOptions.value = response.data.map((scorm) => ({
        text: scorm.title,
        value: JSON.stringify({
          id: scorm.id,
          title: scorm.title,
          created_at: scorm.created_at,
          uuid: scorm.uuid,
        }),
      }));

      scormLoaded.value = true;
    }
  } catch (error) {
    console.error("Error fetching SCORM files:", error);
  } finally {
    isLoadingScorm.value = false;
  }
};

// Handle SCORM accordion toggle
const handleScormAccordionToggle = (isOpen) => {
  if (isOpen && !scormLoaded.value) {
    fetchScormFiles();
  }
};

// Handle SCORM selection
const handleScormSelection = (value) => {
  if (value) {
    try {
      const scormData = JSON.parse(value);
      selectedScorm.value = scormData;
      selectedScormValue.value = value;
      uploadedScorm.value = null; // Clear uploaded scorm

      // Emit both UUID and ID
      emit("scorm-updated", {
        uuid: scormData.uuid,
        id: scormData.id,
      });
    } catch (error) {
      console.error("Error parsing SCORM selection:", error);
    }
  } else {
    selectedScorm.value = null;
    selectedScormValue.value = "";
    uploadedScorm.value = null; // Also clear uploaded scorm
    emit("scorm-updated", null);
  }
};

// Remove SCORM selection
const removeScorm = () => {
  selectedScorm.value = null;
  selectedScormValue.value = "";
  uploadedScorm.value = null;
  emit("scorm-updated", null);
};

// Set SCORM method (select or upload)
const setScormMethod = (method) => {
  scormMethod.value = method;
};

// Open upload modal
const openUploadModal = () => {
  showUploadModal.value = true;
};

// Close upload modal
const closeUploadModal = () => {
  showUploadModal.value = false;
  if (uploadModalRef.value) {
    uploadModalRef.value.resetUploadingState();
  }
};

// Handle SCORM file upload
const handleScormUpload = (file) => {
  uploadedScorm.value = file;

  // Clear the other selection
  selectedScorm.value = null;
  selectedScormValue.value = "";

  // Emit the uploaded file to parent
  emit("scorm-updated", {
    uploadedFile: file,
    method: "upload",
  });

  // Close modal
  closeUploadModal();
};

// Update permalink function
const updatePermalink = (event) => {
  permalink.value = event.target.value;
  emit("permalink-updated", permalink.value);
};

// Method to update from parent data
const updateFromParent = (
  newCoverImage,
  newTagSections,
  newPermalink,
  newScormData
) => {
  // Handle cover image
  if (newCoverImage) {
    coverImageUrl.value = newCoverImage;
  } else {
    coverImageUrl.value = null;
    // Make sure file input is reset
    if (fileInputRef.value) {
      fileInputRef.value.value = "";
    }
  }

  // Update permalink if provided
  if (newPermalink !== undefined) {
    permalink.value = newPermalink;
  }

  // Handle SCORM data
  if (
    newScormData &&
    newScormData.method === "upload" &&
    newScormData.uploadedFile
  ) {
    // Handle uploaded SCORM file
    scormMethod.value = "upload";
    uploadedScorm.value = newScormData.uploadedFile;
    selectedScorm.value = null;
    selectedScormValue.value = "";
  } else if (newScormData && (newScormData.uuid || newScormData.scorm_uuid)) {
    // If SCORM options are already loaded, try to find the matching SCORM
    if (scormOptions.value.length > 0) {
      const matchingScorm = scormOptions.value.find((option) => {
        try {
          const scormData = JSON.parse(option.value);
          return (
            scormData.uuid === (newScormData.uuid || newScormData.scorm_uuid)
          );
        } catch (e) {
          return false;
        }
      });

      if (matchingScorm) {
        const scormData = JSON.parse(matchingScorm.value);
        selectedScorm.value = scormData;
        selectedScormValue.value = matchingScorm.value;
        scormMethod.value = "select";
        uploadedScorm.value = null; // Clear uploaded scorm
      } else {
        // If we can't find matching SCORM but have the data, create a temporary display
        if (newScormData.uuid || newScormData.scorm_uuid) {
          selectedScorm.value = {
            id: newScormData.id,
            title: `SCORM Package (${
              newScormData.uuid || newScormData.scorm_uuid
            })`,
            uuid: newScormData.uuid || newScormData.scorm_uuid,
          };
          scormMethod.value = "select";
          uploadedScorm.value = null; // Clear uploaded scorm
        }
      }
    } else {
      // If SCORM options are not loaded yet, load them first then set the selection
      if (!scormLoaded.value && !isLoadingScorm.value) {
        fetchScormFiles().then(() => {
          // After SCORM files are loaded, try to find the matching SCORM again
          const matchingScorm = scormOptions.value.find((option) => {
            try {
              const scormData = JSON.parse(option.value);
              return (
                scormData.uuid ===
                (newScormData.uuid || newScormData.scorm_uuid)
              );
            } catch (e) {
              return false;
            }
          });

          if (matchingScorm) {
            const scormData = JSON.parse(matchingScorm.value);
            selectedScorm.value = scormData;
            selectedScormValue.value = matchingScorm.value;
            scormMethod.value = "select";
            uploadedScorm.value = null; // Clear uploaded scorm
          } else {
            // If we can't find matching SCORM but have the data, create a temporary display
            if (newScormData.uuid || newScormData.scorm_uuid) {
              selectedScorm.value = {
                id: newScormData.id,
                title: `SCORM Package (${
                  newScormData.uuid || newScormData.scorm_uuid
                })`,
                uuid: newScormData.uuid || newScormData.scorm_uuid,
              };
              scormMethod.value = "select";
              uploadedScorm.value = null; // Clear uploaded scorm
            }
          }
        });
      }
    }
  } else if (!newScormData) {
    selectedScorm.value = null;
    selectedScormValue.value = "";
    uploadedScorm.value = null;
  }

  // Only process tag sections when they exist
  if (newTagSections && newTagSections.length > 0) {
    // Ensure all categories are loaded first
    if (tagSections.length === 0 || isLoading.value) {
      // If categories aren't loaded yet, store the data and process after loading
      const tempTagSections = [...newTagSections];
      // Use a watcher to apply the sections once loading is complete
      const unwatch = watch(isLoading, (newValue) => {
        if (!newValue && tagSections.length > 0) {
          // Now that categories are loaded, process the sections
          processTagSections(tempTagSections);
          // Remove the watcher
          unwatch();
        }
      });
    } else {
      // Categories are already loaded, process directly
      processTagSections(newTagSections);
    }
  }
};

// Helper function to process tag sections
const processTagSections = (newTagSections) => {
  // Map the incoming sections to the existing tagSections
  newTagSections.forEach((newSection) => {
    // Find the matching section in tagSections
    const existingSection = tagSections.find(
      (section) =>
        section.id === newSection.id || section.title === newSection.title
    );

    if (existingSection) {
      // Ensure the section options are loaded if needed
      if (
        !loadedOptions.value.has(existingSection.id) &&
        existingSection.id !== "standards"
      ) {
        fetchCategoryOptions(existingSection.id).then(() => {
          // After options are loaded, update the selections
          updateSectionSelections(existingSection, newSection.selected);
        });
      } else if (existingSection.id === "standards" && !standardsLoaded.value) {
        // Handle standards section separately
        fetchStandardsCategories().then(() => {
          updateSectionSelections(existingSection, newSection.selected);
        });
      } else {
        // Options already loaded, update selections immediately
        updateSectionSelections(existingSection, newSection.selected);
      }
    }
  });
};

// Helper function to update section selections
const updateSectionSelections = (section, selectedItems) => {
  if (!selectedItems || !selectedItems.length) return;

  // Process each selected item to ensure it's in the correct format
  const formattedItems = selectedItems.map((item) => {
    // If the item is already a string that looks like JSON, keep it as is
    if (typeof item === "string" && item.startsWith("{")) {
      return item;
    }

    // Otherwise, try to convert it to our expected format
    try {
      // If it's already a parsed object with id and title
      if (typeof item === "object" && item.id) {
        return JSON.stringify(item);
      }

      // If it's a string but not JSON formatted, create a new object
      return JSON.stringify({
        id: item,
        title: item,
      });
    } catch (e) {
      console.error("Error formatting selected item:", e);
      return item; // fallback to original value
    }
  });

  // Update the section with the formatted selections
  section.selectedItems = formattedItems;
  section.selectedValues = [...formattedItems];

  // Force UI update
  setTimeout(() => {
    section.selectedValues = [...formattedItems];
  }, 0);
};

// Expose the method to parent
defineExpose({
  updateFromParent,
});

// Handle multiple selection change
const handleMultipleSelectionChange = (section) => {
  // Update selectedItems to match selectedValues
  section.selectedItems = [...section.selectedValues];
  emitUpdatedTagSections();
};

// Remove a tag from selected items
const removeTag = (section, index) => {
  // Remove from selectedItems
  section.selectedItems.splice(index, 1);

  // Reset selectedValues completely to ensure sync
  section.selectedValues = [...section.selectedItems];

  // Force BaseInput to update by creating a new array
  // This ensures the checkboxes reflect the current state
  setTimeout(() => {
    // Small delay to ensure DOM updates
    section.selectedValues = [...section.selectedItems];
  }, 0);

  emitUpdatedTagSections();
};

// Emit updated tag sections
const emitUpdatedTagSections = () => {
  // Format the tag sections for emit
  const formattedTagSections = tagSections.map((section) => ({
    id: section.id,
    title: section.title,
    selected: section.selectedItems || [],
  }));

  emit("tag-sections-updated", formattedTagSections);
};

// Watch for changes in tagSections and emit updates
watch(
  tagSections,
  () => {
    emitUpdatedTagSections();
  },
  { deep: true }
);

// Emit initial cover image if available
if (coverImageUrl.value) {
  emit("cover-image-updated", coverImageUrl.value);
}

// Fetch categories when component is mounted
onMounted(() => {
  fetchCategories();
});
</script>

<style>
/* Unscoped: teleported dropdowns render outside this component's scope */
.select-dropdown.variant-form {
  height: auto;
  width: auto;
  min-width: 360px;
}
</style>

<style scoped>

.right-sidebar-title {
  width: 100%;
  margin-left: 5px;
  font-weight: 500;
  font-size: 1em;
  color: var(--primary-blue);
  letter-spacing: 0.01em;
}

.right-sidebar-item {
  border-bottom: 1px solid #d8dae6;
  padding-top: 1rem;
  padding-bottom: 1rem;
  transition: background-color 0.2s ease;
}

/* Loading and Empty States */
.loading-container,
.empty-state {
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 0.9em;
  border-bottom: 1px solid #d8dae6;
}

.loading-container p,
.empty-state p {
  margin: 0;
}

.empty-tags-message {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  padding: 8px 0;
}

.options-loading-indicator {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  margin-top: 5px;
  text-align: center;
  padding: 5px;
  background-color: rgba(0, 61, 106, 0.03);
  border-radius: 4px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Cover Image Styles */
.image-preview-container {
  margin-bottom: 12px;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  background-color: #f8f9fa;
  position: relative;
  display: flex;
  flex-direction: column;
}

.empty-image-message {
  padding: 15px;
  background-color: rgba(0, 61, 106, 0.03);
  border: 1px dashed rgba(0, 61, 106, 0.2);
  border-radius: 6px;
  color: #666;
  font-size: 0.9em;
  text-align: center;
  margin-bottom: 12px;
}

.remove-image-button {
  margin-top: 8px;
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  align-self: center;
  margin-bottom: 8px;
}

.remove-image-button:hover {
  background-color: rgba(220, 53, 69, 0.2);
}

.image-preview {
  max-width: 100%;
  max-height: 160px;
  border-radius: 6px;
  display: block;
  object-fit: cover;
  width: 100%;
}

.upload-button {
  display: inline-block;
  padding: 10px 18px;
  background-color: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  font-size: 0.9em;
  font-weight: 500;
  transition: all 0.2s ease;
  width: fit-content;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.upload-button:hover {
  background-color: #00497e;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.upload-button:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Select Container Styles */
.select-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (min-width: 768px) {
  .select-container {
    flex-direction: row;
    align-items: center;
  }

  .select-container .input-wrap {
    flex-grow: 1;
  }

  .select-container .add-button {
    min-width: 60px;
    width: auto;
    margin-top: 0;
  }
}

/* Tag Styles */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 0; /* Remove minimum height to fix spacing */
}

.tags-container:not(:empty) {
  margin-bottom: 12px; /* Add margin only if there are tags */
  background-color: rgba(0, 61, 106, 0.03);
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 61, 106, 0.07);
}

.tag {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.07) 0%,
    rgba(0, 61, 106, 0.12) 100%
  );
  color: var(--primary-blue);
  padding: 6px 12px;
  border-radius: 18px;
  font-size: 0.85em;
  font-weight: 500;
  border: 1px solid rgba(0, 61, 106, 0.1);
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.tag:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.1) 0%,
    rgba(0, 61, 106, 0.15) 100%
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.delete-tag-button {
  background: rgba(255, 255, 255, 0.6);
  border: none;
  color: var(--primary-blue);
  margin-left: 6px;
  cursor: pointer;
  font-size: 0.6em;
  line-height: 1;
  padding: 1px;
  opacity: 0.8;
  transition: all 0.2s ease;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-tag-button:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.9);
  color: #e63946;
}

.add-button {
  padding: 10px 16px;
  background: linear-gradient(135deg, #00925e 0%, #007c50 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.add-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a06a 0%, #008a59 100%);
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.add-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.add-button:disabled {
  background: linear-gradient(135deg, #e2e2e2 0%, #d8d8d8 100%);
  color: #999;
  cursor: not-allowed;
  box-shadow: none;
}

/* Add subtle animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.tag {
  animation: fadeIn 0.3s ease;
}

.image-preview {
  animation: fadeIn 0.5s ease;
}

/* Permalink styles */
.permalink-container {
  width: 100%;
}

.permalink-helper {
  font-size: 0.85em;
  color: #666;
  margin-top: 5px;
  padding: 5px;
  background-color: rgba(0, 61, 106, 0.03);
  border-radius: 4px;
}

.permalink-input {
  width: 100%;
}

/* SCORM Styles */
.selected-scorm-container {
  margin-bottom: 12px;
  background-color: rgba(0, 61, 106, 0.03);
  border: 1px solid rgba(0, 61, 106, 0.1);
  border-radius: 8px;
  padding: 12px;
}

.selected-scorm-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.scorm-info {
  flex: 1;
}

.scorm-info h4 {
  margin: 0 0 4px 0;
  font-size: 0.95em;
  font-weight: 600;
  color: var(--primary-blue);
}

.scorm-date {
  margin: 0;
  font-size: 0.8em;
  color: #666;
}

.remove-scorm-button {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: none;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: 32px;
  height: 32px;
}

.remove-scorm-button:hover {
  background: rgba(220, 53, 69, 0.2);
}

.empty-scorm-message {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  padding: 8px 0;
  margin-bottom: 12px;
}

.scorm-loading-indicator {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  margin-top: 5px;
  text-align: center;
  padding: 5px;
  background-color: rgba(0, 61, 106, 0.03);
  border-radius: 4px;
  animation: pulse 1.5s infinite;
}

/* SCORM Method Toggle Styles */
.scorm-method-toggle {
  display: flex;
  gap: 2px;
  margin-bottom: 16px;
  border: 1px solid rgba(0, 61, 106, 0.2);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 61, 106, 0.05);
}

.method-button {
  flex: 1;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: #666;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.method-button:hover {
  background: rgba(0, 61, 106, 0.1);
  color: var(--primary-blue);
}

.method-button.tab-active {
  background: var(--primary-blue);
  color: white;
  box-shadow: 0 2px 4px rgba(0, 61, 106, 0.2);
}

.method-button.tab-active:hover {
  background: #00497e;
}

/* Upload Container Styles */
.upload-container {
  margin-top: 12px;
}

.upload-scorm-button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9em;
  transition: all 0.2s ease;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.upload-scorm-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.upload-scorm-button:disabled {
  background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* SCORM Info Styles */
.scorm-type {
  margin: 4px 0 0 0;
  font-size: 0.8em;
  color: #10b981;
  font-weight: 500;
}
</style>
